import React, { useContext } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { percent, formatDate } from '@perch-labs/warbler';
import { Card, Icon, Text } from '@perch-labs/shrike';
import { CleanKwh } from 'contexts/clean-kwh';
import { copyFor, routes } from 'config';
import { ErrorModule } from 'components/errors';
import { LoadingModule } from 'components/loading';
import { useGridStats } from 'hooks/use-grid-stats.hook';
import { useImpactStats } from 'hooks/use-impact-stats.hook';
import { EnergySourceBreakdown } from '../energy-source-breakdown';

const getCopy = copyFor('modules.energyAnalysis');

interface Props {
  withLink?: boolean;
}

export const UsageAnalysis = ({ withLink = true }: Props) => {
  const { planId } = useParams<{ planId?: string }>();
  const {
    cleanKwh,
    loading: cleanKwhLoading,
    error: cleanKwhError,
  } = useContext(CleanKwh);
  const {
    data: impactStats,
    loading: impactStatsLoading,
    error: impactStatsError,
  } = useImpactStats({
    planId: Number(planId),
    endDate: { lteq: cleanKwh?.endDate, gteq: null },
    startDate: { gteq: cleanKwh?.startDate, lteq: null },
    skip: !cleanKwh,
  });
  const {
    data: gridStats,
    loading: gridStatsLoading,
    error: gridStatsError,
  } = useGridStats();
  const history = useHistory();
  const { energyAnalysis } = routes({ planId });

  if (cleanKwhLoading || gridStatsLoading || impactStatsLoading) {
    return <LoadingModule />;
  }
  if (
    cleanKwhError ||
    gridStatsError ||
    impactStatsError ||
    !gridStats ||
    !impactStats
  ) {
    return <ErrorModule title={getCopy('title')} />;
  }

  const fossilAndNuclearUSPercent = gridStats
    ? percent(gridStats.energySummary.fossilFuelsAndNuclear, 0)
    : null;

  const icon = (
    <Icon
      icon="ChevronRight"
      scale={3}
      title={getCopy('title')}
      color="secondary"
    />
  );

  const weatherAdjective =
    cleanKwh.featuredWindProject.projectType === 'solar'
      ? getCopy('description.weather.solar')
      : getCopy('description.weather.wind');

  let headerProps = {
    title: getCopy('title'),
  };
  if (withLink) {
    headerProps = {
      ...headerProps,
      // @ts-expect-error ts-migrate(2322) FIXME: Type '{ icon: JSX.Element; ... Remove this comment to see the full error message
      icon,
      onClick: () => history.push(energyAnalysis),
      to: energyAnalysis,
    };
  }

  return (
    <Card mobileVariant="flat">
      <Card.Header {...headerProps} />
      <Card.Content>
        <Text margin={{ bottom: '16px' }}>
          {getCopy('description.text', {
            cleanKwh: cleanKwh.kwh,
            endDate: formatDate(cleanKwh.endDate, { format: 'MMM d' }),
            featuredWindProjectState: cleanKwh?.featuredWindProject.state,
            fossilAndNuclearUSPercent,
            startDate: formatDate(cleanKwh.startDate, { format: 'MMM d' }),
            weatherAdjective,
          })}
        </Text>
        <EnergySourceBreakdown
          energyBreakdown={gridStats.energyBreakdown}
          fuelTypeEquivalency={impactStats.fuelTypeEquivalency}
          withLink={withLink}
        />
      </Card.Content>
    </Card>
  );
};
